<script>
import { WIDGET_TYPE_TO_PROPS } from '@models/widgets.js';
import { Dialog } from 'quasar';
import { mapState, mapActions } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useAssetsStore } from '@stores/assets';

export default {
    name: 'EditDashboard',
    computed: {
        ...mapState(useAssetsStore, ['assets'])
    },
    data() {
        return {
            dragging: false,
            isEdit: false,
            showWidgetDialog: false,
            originalDashboard: {},
            WIDGET_TYPE_TO_PROPS,
            selectedWidget: null,
            editWidgetIndex: null,
            newWidgets: []
        };
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        /**
         * Add widget.
         * @param {String} - Widget type.
         */
        handleAddWidget(widgetType) {
            if (this.selectedWidget !== null && this.editWidgetIndex !== null) {
                // remove widget from newWidgets before updating
                const widgetIndex = this.newWidgets.indexOf(
                    this.selectedWidget
                );
                if (widgetIndex !== -1) {
                    this.newWidgets.splice(widgetIndex, 1);
                }
                // update widget
                this.dashboard[this.roleView].splice(this.editWidgetIndex, 1, {
                    type: widgetType
                });
            } else {
                // add widget
                this.dashboard[this.roleView].unshift({
                    type: widgetType
                });
            }
            this.newWidgets.push(widgetType);
        },
        /**
         * Create a new widget while editing.
         * @param {String} - Widget type.
         */
        handleAddWidgetWhileEditing(widgetType) {
            this.selectedWidget = null;
            this.editWidgetIndex = null;
            // open dialog
            this.handleAddWidget(widgetType);
        },
        /**
         * Save changes to the server.
         */
        async saveWidgetChanges() {
            // begin load
            this.beginLoading(
                `${this.$t('Updating dashboard. Please wait')}...`
            );
            const payload = {
                dashboard: this.dashboard
            };
            await this.$axios
                .patch(`assets/${this.assetId}/`, payload)
                .then(response => {
                    // update asset field in store (Pinia direct state update)
                    if (this.assets[this.assetId]) {
                        this.assets[this.assetId].dashboard = JSON.parse(
                            JSON.stringify(this.dashboard)
                        );
                    }
                    // notify user
                    this.$notifySuccess(this.$t('Dashboard updated.'));
                })
                .catch(error => {
                    this.$notifyError(
                        this.$t(
                            'Failed to update dashboard. Please try again later.'
                        ),
                        error
                    );
                })
                .finally(() => {
                    // exit edit mode
                    this.isEdit = false;
                    // reset role option
                    this.roleOption = this.userRole;
                    // end loading
                    this.stopLoading();
                    // clear new widget
                    this.newWidgets = [];
                });
        },
        /**
         * Handle dashboard in edit mode.
         */
        handleEditDashboard() {
            this.isEdit = true;
            // store a copy of the original dashboard (deep copy to prevent modifying value in store)
            this.originalDashboard = JSON.parse(
                JSON.stringify(this.asset['dashboard'])
            );

            // filter out widgets created on the backend that are not in the WIDGET_TYPE_TO_PROPS list
            for (const roleview in this.originalDashboard) {
                this.originalDashboard[roleview] = this.originalDashboard[
                    roleview
                ].filter(widget => {
                    return this.WIDGET_TYPE_TO_PROPS[widget['type']];
                });
            }
        },
        /**
         * Revert to the original widgets before editing.
         */
        discardWidgetChanges() {
            // confirm with user
            const dialog = Dialog.create({
                message: this.$t(
                    'Are you sure you want to discard your changes?'
                ),
                title: this.$t('Revert to original widgets'),
                ok: {
                    color: 'primary',
                    label: this.$t('Confirm')
                },
                cancel: {
                    color: 'white',
                    textColor: 'primary',
                    outline: true,
                    label: this.$t('Cancel')
                },
                class: 'q-px-sm q-pb-sm'
            });
            // handle confirmation
            dialog.onOk(() => {
                // revert to original dashboard
                this.dashboard = JSON.parse(
                    JSON.stringify(this.originalDashboard)
                );
                // exit edit mode
                this.isEdit = false;
                // reset role option
                this.roleOption = this.userRole;
            });
        },
        /**
         * Remove a widget from the dashboard.
         * @param {String} - Widget type.
         */
        handleRemoveWidget(widgetType) {
            // find index of widget
            const index = this.dashboard[this.roleView].findIndex(
                w => w.type === widgetType
            );
            // remove widget
            this.dashboard[this.roleView].splice(index, 1);

            // clear selected widget and edit index
            this.selectedWidget = null;
            this.editWidgetIndex = null;
        },
        /**
         * Handle editing a widget.
         * @param {String} - Widget type.
         * @param {Number} - Widget index.
         */
        handleEditWidget(widgetType, widgetIndex) {
            this.selectedWidget = widgetType;
            this.editWidgetIndex = widgetIndex;
            // open dialog
            this.showWidgetDialog = true;
        },
        /**
         * Get badge type for widget if it is a duplicate or new.
         * @param {String} - Widget type.
         * @returns {String} - Badge type.
         */
        getBadgeType(widgetType) {
            // only show badge if in edit mode
            if (!this.isEdit) {
                return '';
            }

            const widgets = this.dashboard[this.roleView].filter(
                widget => widget.type === widgetType
            );

            if (widgets.length > 1) {
                return 'duplicate';
            } else if (this.newWidgets.includes(widgetType)) {
                return 'new';
            }
            return '';
        }
    }
};
</script>
